// Company-prefixed browser routes — /<slug>, /<slug>/home, /<slug>/home/app,
// /<slug>/menu[/<locationId>] — and their un-prefixed legacy forms. Pure, so
// main.tsx's router, the pages and the tests share one parser. The slug rule
// and the reserved list mirror backend/src/lib/tenantSlug.ts.

export const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$/;

/** First path segments that belong to the app itself, never a company. */
export const RESERVED_FIRST_SEGMENTS = new Set([
  'app', 'auth', 'setup', 'operator', 'menu', 'home', 'privacy', 'terms',
  'assets', 'api', 'public', 'admin', 'login', 'logout', 'static', 'index',
]);

export type WebPage = 'landing' | 'home' | 'app' | 'menu' | 'auth';
export interface WebRoute { slug?: string; page: WebPage | null; locationId?: string }

export function isSlug(s: string): boolean {
  return SLUG_RE.test(s) && !RESERVED_FIRST_SEGMENTS.has(s);
}

function segmentsOf(pathname: string): string[] {
  return pathname.split('/').filter(Boolean).map(s => { try { return decodeURIComponent(s); } catch { return s; } });
}

export function parseWebRoute(pathname: string): WebRoute {
  const segs = segmentsOf(pathname);
  if (segs.length === 0) return { page: null };
  const first = segs[0].toLowerCase();
  if (first === 'auth') return { page: 'auth' };
  let slug: string | undefined;
  let rest = segs;
  if (!RESERVED_FIRST_SEGMENTS.has(first)) {
    if (!isSlug(first)) return { page: null };
    slug = first;
    rest = segs.slice(1);
  }
  const [a, b] = rest;
  if (!a) return slug ? { slug, page: 'landing' } : { page: null };
  if (a === 'home') return b === 'app' ? { slug, page: 'app' } : b ? { slug, page: null } : { slug, page: 'home' };
  if (a === 'menu') return b ? { slug, page: 'menu', locationId: b } : { slug, page: 'menu' };
  return { slug, page: null };
}

export function withSlug(slug: string | undefined, path: string): string {
  return slug ? `/${slug}${path}` : path;
}
export const landingPath = (slug: string) => `/${slug}`;
export const homePath = (slug?: string) => withSlug(slug, '/home');
export const menuPath = (slug?: string, locationId?: string) =>
  withSlug(slug, locationId ? `/menu/${encodeURIComponent(locationId)}` : '/menu');

// ---- Directory entry parameters (?view=&tab=&location=&admin=&action=)

export type UserTab = 'dial' | 'numbers' | 'callflows';
export type AdminTab = 'users' | 'groups' | 'numbers' | 'locations' | 'callflows' | 'branding' | 'audit';
export type AppAction = 'import' | 'sync';
export interface AppParams {
  view: 'user' | 'admin';
  initialUserTab?: UserTab;
  initialLocationId?: string;
  initialAdminTab?: AdminTab;
  initialAction?: AppAction;
}

const USER_TABS: UserTab[] = ['dial', 'numbers', 'callflows'];
const ADMIN_TABS: AdminTab[] = ['users', 'groups', 'numbers', 'locations', 'callflows', 'branding', 'audit'];
const ACTIONS: AppAction[] = ['import', 'sync'];

function oneOf<T extends string>(value: string | null, allowed: readonly T[]): T | undefined {
  return value && (allowed as readonly string[]).includes(value) ? (value as T) : undefined;
}

export function parseAppParams(search: string): AppParams {
  const q = new URLSearchParams(search);
  const location = (q.get('location') || '').trim();
  return {
    view: q.get('view') === 'admin' ? 'admin' : 'user',
    initialUserTab: oneOf(q.get('tab'), USER_TABS),
    initialLocationId: location && location.length <= 80 ? location : undefined,
    initialAdminTab: oneOf(q.get('admin'), ADMIN_TABS),
    initialAction: oneOf(q.get('action'), ACTIONS),
  };
}

export function buildAppPath(slug: string | undefined, params: Partial<AppParams> = {}): string {
  const q = new URLSearchParams();
  if (params.view === 'admin') q.set('view', 'admin');
  if (params.initialUserTab) q.set('tab', params.initialUserTab);
  if (params.initialLocationId) q.set('location', params.initialLocationId);
  if (params.initialAdminTab) q.set('admin', params.initialAdminTab);
  if (params.initialAction) q.set('action', params.initialAction);
  const qs = q.toString();
  return withSlug(slug, '/home/app') + (qs ? `?${qs}` : '');
}

/** A same-origin path we are willing to navigate back to after sign-in. */
export function isSafeReturnPath(path: string): boolean {
  return /^\/(?!\/)/.test(path) && !/[\\\s]/.test(path) && !/^\/[^/?#]*:/.test(path);
}

// ---- Return from the Entra admin-consent endpoint (?admin_consent=True / ?error=)

export type ConsentResult = { kind: 'granted' } | { kind: 'error'; message: string };

export function parseConsentResult(search: string): ConsentResult | null {
  const q = new URLSearchParams(search);
  const error = q.get('error');
  if (error) return { kind: 'error', message: q.get('error_description') || error };
  if ((q.get('admin_consent') || '').toLowerCase() === 'true') return { kind: 'granted' };
  return null;
}
